import API from 'utils/api';
import getFormattedValues from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import { API_SCHEME, API_HOST, API_PATH } from 'utils/env';
import { ESTATUS_AUTORIZADO } from 'utils/constants';

export const baseURI = 'nominas/descuentos-empleados';
const optimizedURIs = '/nominas/descuentos-empleados-vista-optimized/';
const exportarExcelURI = '/nominas/descuentos-empleados-exportar-excel/';

const getValuesAndConfig = (values) => {
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };
  const formattedValues = getFormattedValues(values, {
    formData: config.headers,
    clean: false,
  });
  return [formattedValues, config];
};

/**
 * GET Descuentos
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getDescuentos = async (params) => {
  try {
    const response = await API.get(baseURI, { params });
    return response.data;
  } catch (err) {
    onError(err);
  }
  return [];
};
/**
 * GET Descuentos Optimized
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getDescuentosOptimized = async (params) => {
  try {
    const response = await API.get(optimizedURIs, { params });
    return response.data;
  } catch (err) {
    onError(err);
  }
  return [];
};

/**
 * GET Descuentos Empleados -> Excel (todas las filas que cumplen los filtros actuales,
 * generado en el servidor -- ver nominas/views/descuentos_empleados.py,
 * exportarDescuentosEmpleadosVistaExcel). No usa el "Exportar > Excel" genérico de
 * Table.jsx porque ese exporta lo que traiga `data`, y con la paginación en servidor
 * `data` solo trae la página visible.
 * @param {object} params mismos params que getDescuentosOptimized (tab/sujeto/búsqueda),
 * sin `page`/`page_size`.
 * @returns {Promise<Blob|null>}
 */
export const exportarDescuentosEmpleadosExcel = async (params) => {
  try {
    const response = await API.get(exportarExcelURI, {
      params,
      responseType: 'blob',
    });
    return response.data;
  } catch (err) {
    onError(err);
  }
  return null;
};

/**
 * GET Descuento
 * @param {number} id
 * @returns {Promise<object|null>}
 */
export const getDescuento = async (id) => {
  try {
    const response = await API.get(`${baseURI}/${id}`);
    return response.data;
  } catch (err) {
    onError(err);
  }
  return null;
};

/**
 * POST Descuento
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const postDescuento = async (values) => {
  try {
    const response = await API.post(baseURI, ...getValuesAndConfig(values));
    onSuccess(response, 'Agregado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Descuento
 * @param {number} id
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const patchDescuento = async (id, values) => {
  try {
    const response = await API.patch(`${baseURI}/${id}`, ...getValuesAndConfig(values));
    onSuccess(response, 'Editado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * DELETE Descuento
 * @param {number} id
 * @returns {Promise<boolean>}
 */
export const deleteDescuento = async (id) => {
  try {
    const response = await API.delete(`${baseURI}/${id}`);
    onSuccess(response, 'Eliminado correctamente');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Descuentos Plantilla Excel
 * @returns {Promise<object[]>}
 */
export const getDescuentosPlantillaExcel = async () => {
  try {
    const response = await API.get(`${API_SCHEME}://${API_HOST}${API_PATH}nominas/descuentos-obtener-plantilla-excel`, {
      headers: {
        'Content-Type': 'application/ms-excel',
      },
      responseType: 'arraybuffer',
    });
    return response.data;
  } catch (err) {
    onError(err);
  }
  return [];
};

/**
 * PATCH Autorizar Descuento
 * @param {number} id
 * @returns {Promise<object|boolean>}
 */
export const autorizarDescuento = async (id) => {
  try {
    const values = await getDescuento(id);
    const response = await API.patch(`${baseURI}/${id}/`, {
      ...values,
      estados_globales: ESTATUS_AUTORIZADO,
    });
    onSuccess(response, 'Autorizado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
  }
  return false;
};

/**
 * Busca un descuento ya existente por ID de empleado
 * @param {number} empleadoId
 * @returns {Promise<object|null>}
 */
export const findDescuentoPorEmpleado = async (empleadoId) => {
  try {
    const response = await API.get(`${baseURI}`); // trae todos (o usa paginación si es muy grande)
    const descuentos = response.data;

    const descuento = descuentos.find((d) => d.empleado === empleadoId);
    if (!descuento) return null;

    // Luego, obtiene el detalle completo por ID
    return await getDescuento(descuento.id);
  } catch (error) {
    onError(error);
    return null;
  }
};
